import moment from 'moment'
import React, { useState } from 'react'
import { Button, Divider, DropdownProps, Form, Grid, Icon, Image, Label, ListItemProps, Menu, MenuItemProps, Popup, Segment } from 'semantic-ui-react'
import { HtmlLabelProps } from 'semantic-ui-react/dist/commonjs/generic'
import clone from '../../../../assets/svg/clone_entry.svg'
import manual from '../../../../assets/svg/manual_entry.svg'
import select_icon from '../../../../assets/svg/modal_select_icon.svg'
import unselect_icon from '../../../../assets/svg/modal_unselected_icon.svg'
import DropdownComponent from '../../../../components/Dropdown/DropdownComponent'
import DropdownInsideScrollableParent from '../../../../components/Dropdown/DropdownInsideScrollableParent'
import WizardComponent from '../../../../components/Modal/WizardComponent'
import ValidationInput from '../../../../components/ValidationInput/ValidationInput'
import './WizardEnterCosts.css'
import { IUserDetail } from '../../../../types/user'
import { currencyFormat } from '../../../../utils/currencyFormat'
import { IDepartment } from '../../../settings/types/department'
import { initialWizardDirectCost, initialWizardPassThroughCost, initialwizardProjectPhase } from '../../reducers/wizardReducer'
import { IDirectCost, IPassThroughCost } from '../../types/directAndPassTroughCosts'
import { IProject, IProjectPhase } from '../../types/project'
import { IWizardDirectCost, IWizardPassThroughCost, IWizardProject } from '../../types/wizardData'
import { cloneSourceProjects, toCloneProjectOptions, useCloneSourceProjects } from './useCloneSourceProjects'

interface Props {
  open: boolean
  data: IWizardProject
  wizardFinishedSteps: number[]
  setWizardFinishedSteps: (steps: number[]) => void
  setWizardStep: (step: string) => void
  setProjectWizardData: (data: IWizardProject) => void
  onClose: () => void
  wizardCreateProject: (project: IWizardProject) => void
  departments: IDepartment[]
  user: IUserDetail | null
  getDirectCosts: (projectId: number) => Promise<IDirectCost[]>
  getPassTroughCosts: (projectId: number) => Promise<IPassThroughCost[]>
  directCosts: IDirectCost[]
  passTroughCosts: IPassThroughCost[]
  projectClonedState?: boolean
  clonedTasksList?: string[]
}

const _OPTIONS = [
  { id: 1, name: 'Manual entry', type: 'manual', svg: manual },
  { id: 2, name: 'Clone existing project', type: 'clone', svg: clone }
]

const _TABS = [
  { key: 'direct costs', name: 'Direct Costs' },
  { key: 'pass through costs', name: 'Pass Through Costs' }
]

interface IInputValueHelperDc {
  unit_hours: string
  unit_price: string
  quantity: string
}

interface IInputValueHelperPtc {
  unit_price: string
  quantity: string
}

const maxLength25ValidationRule = new RegExp("^.{0,25}$")
const maxLength20ValidationRule = new RegExp("^.{0,20}$")
const maxLength6ValidationRule = new RegExp("^.{0,6}$")
const maxLength100ValidationRule = new RegExp("^.{0,100}$")
const invalidMaxLength25ValidationRuleMsg = 'Max number of characters exceeded. There are only 25 chars allowed.'
const invalidMaxLength20ValidationRuleMsg = 'Max number of characters exceeded. There are only 20 chars allowed.'
const invalidMaxLength6ValidationRuleMsg = 'Max number of characters exceeded. There are only 6 chars allowed.'
const invalidMaxLength100ValidationRuleMsg = 'Max number of characters exceeded. There are only 100 chars allowed.'
const maxLength25 = 25
const maxLength20 = 21
const maxLength6 = 6
const maxLength100 = 100

const WizardEnterCosts = (props: Props): JSX.Element => {
  const [directCostsList, setDirectCostsList] = useState(props.data.directCosts ? props.data.directCosts : [])
  const [passThroughCostsList, setPassThroughCostsList] = useState(props.data.passThroughCosts ? props.data.passThroughCosts : [])
  const [selectedInputOption, setSelectedInputOption] = useState(props.data.input_option)
  const [activeItem, setActiveItem] = useState(props.data.cost_tab ? props.data.cost_tab : 'direct costs')
  const [rebate, setRebate] = useState(props.data.rebate ? props.data.rebate : "0")
  const [internalRebate, setInternalRebate] = useState(props.data.rebate ? +props.data.rebate.toString().replace(',', '.') : 0.00)
  const [selectedProject, setSelectedProject] = useState<IProject | undefined>(undefined)
  const projects = useCloneSourceProjects(selectedInputOption === 'clone')
  const [projectCloned, setProjectCloned] = useState(props.data.projectClonedState ? props.data.projectClonedState : false)
  const [inputValuesDc, setInputValuesDc] = useState<IInputValueHelperDc[]>(props.data.directCosts ? props.data.directCosts.map(dc => {
    return {
      unit_price: dc.unit_price.toString().replace('.', ','),
      unit_hours: dc.unit_hours.toString().replace('.', ','),
      quantity: dc.quantity.toString()
    }
  }) : [])
  const [inputValuesPtc, setInputValuesPtc] = useState<IInputValueHelperPtc[]>(props.data.passThroughCosts ? props.data.passThroughCosts.map(dc => {
    return {
      unit_price: dc.unit_price.toString().replace('.', ','),
      quantity: dc.quantity.toString()
    }
  }) : [])
  const [inputValuesRebate, setInputValuesRebate] = useState<string>(props.data.rebate ? props.data.rebate.toString().replace('.', ',') : '')
  const [clonedTasks, setClonedTasks] = useState<string[]>(props.data.clonedTasksList ? props.data.clonedTasksList : [])
  const [inputFocusedId, setInputFocusedId] = useState<string>('')

  const dcLen = directCostsList.length
  const ptcLen = passThroughCostsList.length

  const onClick = (item: string) => {
    setSelectedInputOption(item)
  }

  const handleItemClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, data: MenuItemProps) => {
    if (data && data.name) {
      if (data.name.toLowerCase() !== activeItem) {
        setActiveItem(data.name.toLowerCase())
      }
    }
  }

  const renderInputOptions = () => {
    return (
      <Grid.Row columns={2} style={{ paddingTop: '.3rem' }}>
        {_OPTIONS.map(o => {
          return (
            <Grid.Column key={o.id}>
              <Segment
                onClick={() => onClick(o.type)}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  opacity: o.type === selectedInputOption ? 1 : .6,
                  border: `2px solid ${o.type === selectedInputOption ? '#08D6A1' : '#BEC8C3'}`,
                  backgroundColor: 'transparent',
                  display: 'flex',
                  color: '#232C58'
                }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'left',
                    width: '90%',
                    fontWeight: 'bold'
                  }}
                >
                  <Image style={{ marginLeft: '18px', marginRight: '17px', height: '18.5px', marginBottom: '.2rem' }} src={o.svg} />
                  {o.name}
                </div>
                <Image style={{ height: '18.5px' }} src={o.type === selectedInputOption ? select_icon : unselect_icon} />
              </Segment>
            </Grid.Column>
          )
        })}
      </Grid.Row>
    )
  }

  const renderCostTabs = () => {
    return (
      <Grid.Row columns={1}>
        <Grid.Column>
          <Menu secondary style={{ marginTop: '-1.5rem', position: 'fixed', paddingRight: '.3rem' }}>
            {_TABS.map(tab => {
              return (
                <Menu.Item
                  key={tab.key}
                  name={tab.name}
                  active={activeItem === tab.key}
                  onClick={(e, data) => handleItemClick(e, data)}
                  style={{
                    border: activeItem === tab.key ? '1px solid #08D6A1' : '1px solid #BEC8C3',
                    color: activeItem === tab.key ? '#08D6A1' : '#747678',
                    borderRadius: '.5rem',
                    height: '2.3rem',
                    background: 'transparent'
                  }}
                />
              )
            })}
          </Menu>
        </Grid.Column>
      </Grid.Row>
    )
  }


  // DirectCosts
  const onChangeDirectCost = (e: React.SyntheticEvent<HTMLElement, Event>, data: ListItemProps | HtmlLabelProps, costId: string | number, dropdown: 'phase' | 'department') => {
    const _costs = directCostsList.map(cost => {
      if (cost.id === costId) {
        if (dropdown === 'phase') {
          cost.phase = props.data.phases.filter(p => p.name === data.value)[0]
        } else if (dropdown === 'department') {
          cost.department = props.departments.filter(p => p.name === data.value)[0]
        }
      }
      return cost
    })
    setDirectCostsList(_costs)
  }

  const directCostInputChange = (event: React.ChangeEvent<HTMLInputElement>, key: string | number, inputName: string, idx: number, converted?: number) => {
    const _costs = directCostsList.map(cost => {
      if (cost.id === key) {
        if (inputName === 'key') {
          cost.key = event.target.value
        } else if (inputName === 'offerPosition') {
          cost.offer_position = event.target.value
        } else if (inputName === 'unit') {
          cost.unit = event.target.value
        } else if (inputName === 'quantity') {
          cost.quantity = converted ?? 0
        } else if (inputName === 'unitHours') {
          cost.unit_hours = converted ?? 0
        } else if (inputName === 'unitPrice') {
          cost.unit_price = converted ?? 0
        } else if (inputName === 'total') {
          event.preventDefault()
        }
      }
      return cost
    })
    if (inputName === 'quantity' || inputName === 'unitHours' || inputName === 'unitPrice') {
      const _key = inputName === 'unitHours' ? 'unit_hours' : inputName === 'unitPrice' ? 'unit_price' : 'quantity'
      const _values = inputValuesDc.map((iv, i) => {
        if (idx === i) {
          return { ...iv, [_key]: event.target.value }
        }
        return iv
      })
      setInputValuesDc(_values)
    }
    setDirectCostsList(_costs)
  }

  const rebateInputChange = (event: React.ChangeEvent<HTMLInputElement>, converted: number) => {
    let value: number | string = converted
    let inputValue: string = event.target.value
    if (value <= 100 && (value.toString().indexOf('.') === 1 ? value.toString().length < 5 : value.toString().length < 6)) {
      if (internalRebate === 0 && value.toString().indexOf('0') === 0 && value.toString().length === 2) {
        value = value.toString().substring(1, 2)
        inputValue = inputValue.substring(1, 2)
      }
      setRebate(value)
      setInternalRebate(+value)
      setInputValuesRebate(inputValue.replace('.', ','))
    }
  }

  const renderKeyInput = (item: IWizardDirectCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        value={item.key}
        onChange={e => directCostInputChange(e, item.id, 'key', idx)}
        placeholder='Key'
        fieldWidth={1}
        label={renderLabels('Key', '', 0, idx, false, true)}
        paddingRight='.2rem'
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        validatePattern={maxLength6ValidationRule}
        invalidMessage={invalidMaxLength6ValidationRuleMsg}
        maxLength={maxLength6}
        autoExpand={true}
        autoExpandMax={120}
      />
    )
  }

  const renderOfferPositionInput = (item: IWizardDirectCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        value={item.offer_position}
        onChange={e => directCostInputChange(e, item.id, 'offerPosition', idx)}
        placeholder='Enter Position'
        fieldWidth={3}
        label={renderLabels('Offer Position', '', 1, idx, false, true)}
        paddingRight='.2rem'
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        validatePattern={maxLength25ValidationRule}
        invalidMessage={invalidMaxLength25ValidationRuleMsg}
        maxLength={maxLength25}
        autoExpand={true}
        autoExpandMax={350}
      />
    )
  }

  const renderUnitInput = (item: IWizardDirectCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        value={item.unit}
        onChange={e => directCostInputChange(e, item.id, 'unit', idx)}
        placeholder='Unit'
        fieldWidth={2}
        label={renderLabels('Unit', '', 11, idx, false, true)}
        paddingRight='.2rem'
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        validatePattern={maxLength100ValidationRule}
        invalidMessage={invalidMaxLength100ValidationRuleMsg}
        maxLength={maxLength100}
        autoExpand={true}
        autoExpandMax={350}
      />
    )
  }

  const renderPhaseLabel = (phase: IProjectPhase) => {
    const _upperCase = phase.name.toUpperCase() === phase.name ? true : false
    const _nameLen = _upperCase ? phase.name : phase.name.length > 14 ? phase.name.slice(0, 14).concat('...') : phase.name
    return (
      <Label
        key={phase.id}
        horizontal
        style={{
          borderRadius: '.6rem',
          color: phase.color,
          backgroundColor: phase.color + '33',
          fontSize: '.8rem',
          height: '1.7rem',
          minWidth: '4rem',
          maxWidth: '9.1rem',
          whiteSpace: 'nowrap'
        }}
        content={activeItem === 'pass through costs' ? phase.name : _nameLen}
      />
    )
  }

  const renderPhasePopup = (activityId: string | number, phase: IProjectPhase, idx: number) => {
    const _costs = directCostsList.filter(a => a.id === activityId)[0]
    const _options = props.data.phases.map(p => {
      // add opacity 30% to phase color
      const _lightColor = p.color + '33'
      const _upperCase = p.name.toUpperCase() === p.name ? true : false
      const _nameLen = _upperCase ? p.name.length > 17 ? p.name.slice(0, 16).concat('...') : p.name : p.name.length > 21 ? p.name.slice(0, 20).concat('...') : p.name
      return {
        key: p.id,
        text: (<Label key={p.id} horizontal style={{ color: p.color, backgroundColor: _lightColor, fontSize: '.8rem', height: '1.7rem', borderRadius: '.6rem' }} content={_nameLen} />),
        value: p.name,
        selected: _costs.phase.name === p.name
      }
    }).sort((a, b) => a.value.localeCompare(b.value))
    return (
      <Form.Field width={2} style={{ paddingRight: 0 }} key={`${idx}-phase`}>
        {renderLabels('Phase', 'Select phase', 2, idx, false, true)}
        <DropdownInsideScrollableParent
          iconFontSize='1.2rem'
          iconName='caret down'
          iconColor='#08D6A1'
          options={_options}
          content={renderPhaseLabel(phase)}
          name={phase.name}
          placeholder='Set a phase'
          marginTop={idx === 0 ? '2rem' : ''}
          width={'9.1rem'}
          buttonWidth='9rem'
          onClick={(e, data) => onChangeDirectCost(e, data, activityId, 'phase')}
        />
      </Form.Field>
    )
  }

  const renderDepartmentPopup = (costId: string | number, department: IDepartment, idx: number) => {
    const _activity = directCostsList.filter(cost => cost.id === costId)[0]
    const _options = props.departments.map(d => {
      const _upperCase = d.name.toUpperCase() === d.name ? true : false
      const _name = _upperCase ? d.name.length > 15 ? d.name.slice(0, 14).concat('...') : d.name : d.name.length > 19 ? d.name.slice(0, 16).concat('...') : d.name
      return {
        key: d.id,
        text: _name,
        value: d.name,
        selected: _activity.department.name === d.name
      }
    })
    const _upperCase = department.name.toUpperCase() === department.name ? true : false
    const _name = _upperCase ? department.name.length > 14 ? department.name.slice(0, 13).concat('...') : department.name : department.name.length > 18 ? department.name.slice(0, 15).concat('...') : department.name
    return (
      <Form.Field width={2} style={{ paddingRight: 0 }} key={`${idx}-department`}>
        {renderLabels('Department', 'Select department', 3, idx, false, true)}
        <DropdownInsideScrollableParent
          iconFontSize='1.2rem'
          iconName='caret down'
          iconColor='#08D6A1'
          options={_options}
          content={_name}
          placeholder='Enter department'
          marginTop={idx === 0 ? '2rem' : ''}
          width={'11rem'}
          buttonWidth='11rem'
          onClick={(e, data) => onChangeDirectCost(e, data, costId, 'department')}
        />
      </Form.Field>
    )
  }

  const renderQuantityInput = (item: IWizardDirectCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        type='number'
        value={inputValuesDc[idx].quantity}
        onChange={(e, v, c) => directCostInputChange(e, item.id, 'quantity', idx, c)}
        placeholder='Enter #'
        fieldWidthPercentage='9.375%'
        label={renderLabels('Quantity', '', 4, idx, false, true)}
        paddingRight={0}
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        twoDecimalPlaces
        autoExpand={true}
        autoExpandMax={200}
      />
    )
  }

  const renderUnitHoursInput = (item: IWizardDirectCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        type='number'
        value={inputValuesDc[idx].unit_hours}
        onChange={(e, v, c) => directCostInputChange(e, item.id, 'unitHours', idx, c)}
        placeholder='Add hours'
        fieldWidthPercentage='9.375%'
        label={renderLabels('Unit Hours', '', 5, idx, false, true)}
        paddingRight={0}
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        className='wizard-unit-hours'
        twoDecimalPlaces
        autoExpand={true}
        autoExpandMax={200}
      />
    )
  }

  const renderUnitPriceInput = (item: IWizardDirectCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        type='number'
        value={inputValuesDc[idx].unit_price}
        onChange={(e, v, c) => directCostInputChange(e, item.id, 'unitPrice', idx, c)}
        placeholder='Add price'
        fieldWidthPercentage='9.375%'
        label={renderLabels('Unit Price', '', 6, idx, false, true)}
        paddingRight={0}
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        icon='euro'
        iconPosition='left'
        className='number-input font'
        twoDecimalPlaces
        autoExpand={true}
        autoExpandMax={200}
      />
    )
  }

  const renderTotalHoursInput = (item: IWizardDirectCost, idx: number) => {
    const total = +item.unit_hours * +item.quantity
    return (
      <Form.Field style={{ paddingRight: 0, paddingLeft: '2rem', marginLeft: 'auto', minWidth: '9.375%', width: 'auto', textAlign: 'right' }} key={'total-hours'}>
        {idx === 0 && (
          <label style={{ display: 'flex', alignItems: 'center', position: 'fixed', zIndex: directCostsList.length + 1, justifyContent: 'flex-end', width: '6.1%', background: dcLen <= 5 ? '' : '#FFFFFF' }}>
            <p style={{ fontSize: '14px', fontWeight: 'bold', margin: 0, color: '#172533' }}>Hours</p>
          </label>
        )}
        <div style={{ marginRight: '.5rem', fontWeight: 'bold', display: 'flex', justifyContent: 'flex-end', color: '#232C58', marginTop: idx === 0 ? '2rem' : '', height: '2.7rem' }}>
          <p style={{ marginTop: '.6rem', marginRight: '.3rem', whiteSpace: 'nowrap' }}>{total.toFixed(2)} h </p>
        </div>
      </Form.Field>
    )
  }

  const renderTotalCostsInput = (item: IWizardDirectCost, idx: number) => {
    const total = +item.unit_price * +item.quantity
    return (
      <Form.Field style={{ paddingRight: 0, minWidth: '6.25%', width: 'auto', textAlign: 'right' }} key={'total-price'}>
        {idx === 0 && (
          <label style={{ display: 'flex', alignItems: 'center', position: 'fixed', zIndex: directCostsList.length + 1, justifyContent: 'flex-end', width: '5.5%', background: dcLen <= 5 ? '' : '#FFFFFF' }}>
            <p style={{ fontSize: '14px', fontWeight: 'bold', margin: 0, color: '#172533' }}>Costs</p>
          </label>
        )}
        <div style={{ fontWeight: 'bold', display: 'flex', justifyContent: 'flex-end', color: '#232C58', marginTop: idx === 0 ? '2rem' : '', height: '2.7rem', whiteSpace: 'nowrap' }}>
          <p style={{ marginTop: '.6rem', marginRight: '.2rem', color: '#232C58' }}>{currencyFormat(+total)[0]}</p>
          <p style={{ marginTop: '.6rem', color: '#232C58' }}>{currencyFormat(+total).replace(currencyFormat(+total)[0], '')}</p>
        </div>
      </Form.Field>
    )
  }

  const removeDirectCost = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: IWizardDirectCost, idx: number) => {
    const list = directCostsList.filter(i => i.id !== item.id)
    const clonedTasksList = clonedTasks.filter((i: string) => i !== item.offer_position)
    const cloneActive = clonedTasksList.length > 0
    setInputValuesDc(inputValuesDc.filter((iv, i) => i !== idx))
    setDirectCostsList(list)
    setClonedTasks(clonedTasksList)
    setProjectCloned(cloneActive)
  }

  const renderDirectCostRemoveIcon = (item: IWizardDirectCost, idx: number) => {
    if (item.id !== '') {
      return (
        <Form.Field style={{ paddingRight: '1rem', paddingLeft: '1rem', flex: '0 0 30px', maxWidth: '30px' }} key={'remove-icon'}>
          <Button key={idx} icon onClick={e => removeDirectCost(e, item, idx)} style={{ backgroundColor: 'transparent', boxShadow: 'none', marginTop: idx === 0 ? '2rem' : '' }}>
            <Icon name='times circle' color='red' style={{ fontSize: '15px' }} />
          </Button>
        </Form.Field>
      )
    }
    return null
  }

  const renderSubtotal = () => {
    let total = 0.00
    let totalHours = 0
    let totalWithRebate = 0.00
    directCostsList.forEach(cost => total += (+cost.unit_price * +cost.quantity))
    directCostsList.forEach(cost => totalHours += (+cost.unit_hours * +cost.quantity))
    directCostsList.forEach(cost => totalWithRebate += ((+cost.unit_price * +cost.quantity)) * ((100 - internalRebate) / 100))
    return (
      <div style={{ display: 'flex', flexDirection: 'column', float: 'right' }}>
        <Divider section={true} style={{ borderTop: '1px solid #E5EBF0', borderBottom: '1px solid #E5EBF0', margin: 'auto 2rem auto auto', width: '13.8rem', zIndex: '0' }} />
        <div style={{ position: 'fixed', marginTop: '1rem', marginBottom: '-1rem', display: 'flex', alignItems: 'baseline', fontSize: '14px', right: '4rem', gap: '0.7rem' }}>
          <p style={{ fontWeight: 'bold' }}>Subtotal</p>
          <p style={{ fontWeight: 'bold', whiteSpace: 'nowrap', paddingRight: '1rem', textAlign: 'right', minWidth: '8rem' }}>
            {totalHours.toFixed(2)} h
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', fontWeight: 'bold', color: '#232C58', whiteSpace: 'nowrap', minWidth: '5rem' }}>
            <p style={{ marginTop: 0, marginRight: '.2rem', color: '#232C58' }}>{currencyFormat(+total)[0]}</p>
            <p style={{ marginTop: 0, color: '#232C58' }}>{currencyFormat(+total).replace(currencyFormat(+total)[0], '')}</p>
          </div>
        </div>
        <div style={{ position: 'fixed', marginTop: '4rem', marginBottom: '-1rem', display: 'flex', width: '25rem', right: '4rem', justifyContent: 'space-between' }}>
          <p style={{ fontSize: '14px', position: 'fixed', right: '25rem', display: 'flex', alignItems: 'center', height: '2.7rem' }}>Rebate %</p>
          <ValidationInput
            type='number'
            value={inputValuesRebate}
            onChange={(e, v, c) => rebateInputChange(e, c ?? 0)}
            placeholder='0%'
            width={'5rem'}
            paddingRight={0}
            paddingLeft={'4.5rem'}
            zIndex={1}
            symbol='%'
            boldSymbol={true}
          />
          <p style={{
            position: 'fixed',
            marginRight: '-13px',
            right: currencyFormat(+total).length >= 14 ? '13.7rem' :
              currencyFormat(+total).length >= 13 ? '12.8rem' :
                currencyFormat(+total).length >= 12 ? '12rem' : '11rem',
            color: '#232C58',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            Total
          </p>
          <div style={{ display: 'flex', position: 'fixed', marginRight: '0', right: '4rem', color: '#232C58', fontWeight: 'bold', fontSize: '14px' }}>
            <p style={{ marginTop: 0, marginRight: '.2rem', color: '#232C58' }}>{currencyFormat(+totalWithRebate)[0]}</p>
            <p style={{ marginTop: 0, color: '#232C58' }}>{currencyFormat(+totalWithRebate).replace(currencyFormat(+totalWithRebate)[0], '')}</p>
          </div>
        </div>
      </div>
    )
  }

  const renderDirectCostForm = () => {
    return directCostsList.map((i, idx) => {
      return (
        <Form.Group key={i.id} unstackable style={{ width: '100%' }}>
          {renderKeyInput(i, idx)}
          {renderOfferPositionInput(i, idx)}
          {renderPhasePopup(i.id, i.phase, idx)}
          {renderDepartmentPopup(i.id, i.department, idx)}
          {renderUnitInput(i, idx)}
          {renderQuantityInput(i, idx)}
          {renderUnitHoursInput(i, idx)}
          {renderUnitPriceInput(i, idx)}
          {renderTotalHoursInput(i, idx)}
          {renderTotalCostsInput(i, idx)}
          {renderDirectCostRemoveIcon(i, idx)}
        </Form.Group>
      )
    })
  }
  // end DirectCosts

  // PassThroughCosts
  const onChangePassThroughCost = (e: React.SyntheticEvent<HTMLElement, Event>, phase: IProjectPhase, costId: string | number, checked: boolean) => {
    if (!checked) {
      const _costs = passThroughCostsList.map(cost => {
        if (cost.id === costId) {
          cost.phases = [...cost.phases, ...props.data.phases.filter(p => p.name === phase.name)]
        }
        return cost
      })
      setPassThroughCostsList(_costs)
    } else {
      const _costs = passThroughCostsList.map(cost => {
        if (cost.id === costId) {
          cost.phases = cost.phases.filter(p => p.name !== phase.name)
        }
        return cost
      })
      setPassThroughCostsList(_costs)
    }
  }

  const passThroughInputChange = (event: React.ChangeEvent<HTMLInputElement>, key: string | number, inputName: string, idx: number, converted?: number) => {
    const _costs = passThroughCostsList.map(cost => {
      if (cost.id === key) {
        if (inputName === 'offerPosition') {
          cost.offer_position = event.target.value
        } else if (inputName === 'unit') {
          cost.unit = event.target.value
        } else if (inputName === 'supplier') {
          cost.supplier = event.target.value
        } else if (inputName === 'quantity') {
          cost.quantity = converted ?? 0
        } else if (inputName === 'unitPrice') {
          cost.unit_price = converted ?? 0
        } else if (inputName === 'total') {
          event.preventDefault()
        }
      }
      return cost
    })
    if (inputName === 'quantity' || inputName === 'unitPrice') {
      const _key = inputName === 'unitPrice' ? 'unit_price' : 'quantity'
      const _values = inputValuesPtc.map((iv, i) => {
        if (idx === i) {
          return { ...iv, [_key]: event.target.value }
        }
        return iv
      })
      setInputValuesPtc(_values)
    }
    setPassThroughCostsList(_costs)
  }


  const renderOfferPositionPassThroughInput = (item: IWizardPassThroughCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        value={item.offer_position}
        onChange={e => passThroughInputChange(e, item.id, 'offerPosition', idx)}
        placeholder='Enter Position'
        fieldWidth={3}
        label={renderLabels('Offer Position', '', 1, idx, false, true)}
        paddingRight='.2rem'
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        validatePattern={maxLength25ValidationRule}
        invalidMessage={invalidMaxLength25ValidationRuleMsg}
        maxLength={maxLength25}
        autoExpand={true}
        autoExpandMax={350}
      />
    )
  }

  const renderUnitPassThroughInput = (item: IWizardPassThroughCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        value={item.unit}
        onChange={e => passThroughInputChange(e, item.id, 'unit', idx)}
        placeholder='Unit'
        fieldWidth={2}
        label={renderLabels('Unit', '', 11, idx, false, true)}
        paddingRight='.2rem'
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        validatePattern={maxLength100ValidationRule}
        invalidMessage={invalidMaxLength100ValidationRuleMsg}
        maxLength={maxLength100}
        autoExpand={true}
        autoExpandMax={350}
      />
    )
  }

  const renderCheckbox = (checked: boolean) => {
    if (checked) {
      return (
        <Button
          style={{ height: '1.25rem', width: '1.25rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          primary={true}
          icon='check'
        />
      )
    }
    return <div style={{ cursor: 'pointer', marginRight: '0.3rem', background: '#E0E1E2', height: '1.55rem', width: '1.55rem', borderRadius: '0.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }} />
  }

  const renderSelectedPhases = (cost: IWizardPassThroughCost) => {
    const len = props.data.phases.length
    const selectedLen = cost.phases.filter(p => p.name !== '').length
    const calc = len - selectedLen
    const allText = 'All Phases'
    const text = calc === 0 ? allText : `+${selectedLen - 1}`
    if (text !== '+0' && selectedLen > 1) {
      return (
        <div style={{ display: 'flex' }}>
          {text === 'All Phases' ? null : renderPhaseLabel(cost.phases.filter(p => p.name !== '')[0])}
          <Label
            content={(<p style={{ overflowWrap: 'break-word' }}>{text}</p>)}
            style={{
              background: '#E0E0E0',
              color: '#696C72',
              borderRadius: '.57rem',
              maxWidth: '5rem',
              minWidth: '1.5rem',
              padding: '.1rem .4rem',
              textAlign: 'center',
              marginLeft: '0rem'
            }}
          />
        </div>
      )
    }
    return renderPhaseLabel(cost.phases.filter(p => p.name !== '')[0])
  }

  const renderPhasesDropdown = (costId: string | number, phases: IProjectPhase[], idx: number) => {
    const cost = passThroughCostsList.filter(c => +c.id === +costId)[0]
    const _options = props.data.phases.map(phase => {
      return {
        key: phase.id,
        value: phase.id,
        text: (
          <div style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => onChangePassThroughCost(e, phase, costId, phases.map(p => p.id).indexOf(phase.id) > -1)}>
            {renderCheckbox(phases.map(p => p.id).indexOf(phase.id) > -1)}
            <Label
              content={(<p style={{ overflowWrap: 'break-word' }}>{phase.name}</p>)}
              style={{
                background: phase.color + '33',
                color: phase.color,
                borderRadius: '.57rem',
                minWidth: '5rem',
                padding: '.1rem .2rem',
                textAlign: 'center'
              }}
            />
          </div>
        ),
        selected: false
      }
    })
    return (
      <Form.Field width={2} style={{ paddingRight: 0 }} key={`${idx}-phase`}>
        {renderLabels('Phase', 'Select phase', 2, idx, false, true)}
        <DropdownInsideScrollableParent
          iconFontSize='1.2rem'
          iconName='caret down'
          iconColor='#08D6A1'
          options={_options}
          content={cost && cost.phases.filter(p => p.name !== '').length !== 0 ? renderSelectedPhases(cost) : ''}
          placeholder='Set a phase'
          marginTop={idx === 0 ? '2rem' : ''}
          width={'12rem'}
          buttonWidth='12rem'
        />
      </Form.Field>
    )
  }

  const renderSupplierInput = (item: IWizardPassThroughCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        value={item.supplier}
        onChange={e => passThroughInputChange(e, item.id, 'supplier', idx)}
        placeholder='Enter Supplier'
        fieldWidth={3}
        label={renderLabels('Supplier', '', 3, idx, false, true)}
        paddingRight={0}
        paddingLeft='0'
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        validatePattern={maxLength20ValidationRule}
        invalidMessage={invalidMaxLength20ValidationRuleMsg}
        maxLength={maxLength20}
        autoExpand={true}
        autoExpandMax={350}
      />
    )
  }

  const renderQuantityPositionPassThroughInput = (item: IWizardPassThroughCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        type='number'
        value={inputValuesPtc[idx].quantity}
        onChange={(e, v, c) => passThroughInputChange(e, item.id, 'quantity', idx, c)}
        placeholder='Enter #'
        fieldWidthPercentage='9.375%'
        label={renderLabels('Quantity', '', 4, idx, false, true)}
        paddingRight={0}
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        twoDecimalPlaces
        autoExpand={true}
        autoExpandMax={150}
      />
    )
  }

  const renderUnitPricePositionPassThroughInput = (item: IWizardPassThroughCost, idx: number) => {
    return (
      <ValidationInput
        fluid
        type='number'
        value={inputValuesPtc[idx].unit_price}
        onChange={(e, v, c) => passThroughInputChange(e, item.id, 'unitPrice', idx, c)}
        placeholder='Add price'
        fieldWidth={2}
        label={renderLabels('Unit Price', '', 6, idx, false, true)}
        paddingRight={0}
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        icon='euro'
        iconPosition='left'
        className='number-input font'
        twoDecimalPlaces
        autoExpand={true}
        autoExpandMax={200}
      />
    )
  }

  const renderTotalCostsPositionPassThroughInput = (item: IWizardPassThroughCost, idx: number) => {
    const total = +item.unit_price * +item.quantity
    return (
      <Form.Field width={2} style={{ paddingRight: 0, paddingLeft: '1rem', textAlign: 'right' }} key={'total'}>
        {idx === 0 && (
          <label style={{ display: 'flex', alignItems: 'center', position: 'fixed', zIndex: passThroughCostsList.length + 1, justifyContent: 'flex-end', width: '11.2%', background: ptcLen <= 5 ? '' : '#FFFFFF' }}>
            <p style={{ fontSize: '14px', fontWeight: 'bold', margin: 0, color: '#172533' }}>Total Costs</p>
          </label>
        )}
        <div style={{ justifyContent: 'flex-end', fontWeight: 'bold', display: 'flex', color: '#232C58', marginTop: idx === 0 ? '2rem' : '', height: '2.7rem' }}>
          <p style={{ marginTop: '.6rem', marginRight: '.2rem' }}>{currencyFormat(+total)[0]}</p>
          <p style={{ marginTop: '.6rem' }}>{currencyFormat(+total).replace(currencyFormat(+total)[0], '')}</p>
        </div>
      </Form.Field>
    )
  }

  const removePassThrough = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: IWizardPassThroughCost, idx: number) => {
    const list = passThroughCostsList.filter(i => i.id !== item.id)
    const clonedTasksList = clonedTasks.filter((i: string) => i !== item.offer_position)
    const cloneActive = clonedTasksList.length > 0
    setInputValuesPtc(inputValuesPtc.filter((iv, i) => i !== idx))
    setPassThroughCostsList(list)
    setClonedTasks(clonedTasksList)
    setProjectCloned(cloneActive)
  }

  const renderPassThroughRemoveIcon = (item: IWizardPassThroughCost, idx: number) => {
    if (item.id !== '') {
      return (
        <Form.Field style={{ paddingRight: '1rem', paddingLeft: '1rem', marginLeft: '0.5rem', flex: '0 0 30px', maxWidth: '30px' }} key={'remove-icon'}>
          <Button key={idx} icon onClick={e => removePassThrough(e, item, idx)} style={{ backgroundColor: 'transparent', boxShadow: 'none', marginTop: idx === 0 ? '2rem' : '' }}>
            <Icon name='times circle' color='red' style={{ fontSize: '15px' }} />
          </Button>
        </Form.Field>
      )
    }
    return null
  }

  const renderPassThroughCostForm = () => {
    return passThroughCostsList.map((i, idx) => {
      return (
        <Form.Group key={idx} unstackable style={{ width: '100%' }}>
          {renderOfferPositionPassThroughInput(i, idx)}
          {renderPhasesDropdown(i.id, i.phases, idx)}
          {renderSupplierInput(i, idx)}
          {renderUnitPassThroughInput(i, idx)}
          {renderQuantityPositionPassThroughInput(i, idx)}
          {renderUnitPricePositionPassThroughInput(i, idx)}
          {renderTotalCostsPositionPassThroughInput(i, idx)}
          {renderPassThroughRemoveIcon(i, idx)}
        </Form.Group>
      )
    })
  }

  const renderTotal = () => {
    let total = 0.00
    passThroughCostsList.forEach(cost => total += (+cost.unit_price * +cost.quantity))
    return (
      <div style={{ display: 'flex', flexDirection: 'column', float: 'right' }}>
        <Divider section={true} style={{ borderTop: '1px solid #E5EBF0', borderBottom: '1px solid #E5EBF0', margin: 'auto 2rem auto auto', width: '11.5rem', zIndex: 0 }} />
        <div style={{ position: 'fixed', marginTop: '1rem', marginBottom: '-1rem', display: 'flex', alignItems: 'baseline', fontSize: '14px', fontWeight: 'bold', right: '4.8rem', gap: '0.7rem', zIndex: 0 }}>
          <p>Total</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', whiteSpace: 'nowrap', minWidth: '5rem' }}>
            <p style={{ marginTop: 0, marginRight: '.2rem', color: '#232C58' }}>{currencyFormat(+total)[0]}</p>
            <p style={{ marginTop: 0, color: '#232C58' }}>{currencyFormat(+total).replace(currencyFormat(+total)[0], '')}</p>
          </div>
        </div>
      </div>
    )
  }
  // end PassThroughCosts

  const renderLabels = (label: string, popup: string, key: number, idx: number, icon?: boolean, bold?: boolean) => {
    if (idx === 0) {
      return (
        <label key={key}
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'fixed',
            zIndex: directCostsList.length + 1,
            justifyContent: (key === 7 || key === 8) ? 'flex-end' : undefined,
            width:
              (activeItem === 'direct costs' && key === 6) ? '100px' :
              (activeItem === 'pass through costs' && key === 6) ? '120px' : 'inherit',
            background:
              activeItem === 'direct costs' ? dcLen <= 5 ? '' : 'white' :
              activeItem === 'pass through costs' ? ptcLen <= 5 ? '' : 'white' : '' }}>
          <p style={{ fontSize: '14px', fontWeight: bold ? 'bold' : 'normal', margin: 0, color: '#172533' }}>{label}</p>
          {icon ?
            <Popup
              className='popup-info'
              trigger={<Icon name='info circle' size='small' style={{ marginLeft: '2px' }} />}
              content={popup}
              position='right center'
            /> : null}
        </label>
      )
    }
    return null
  }


  const addCost = () => {
    if (activeItem === 'direct costs') {
      const key = directCostsList.length !== 0 ? Math.max(...directCostsList.map(d => +d.id)) + 1 : 0
      const cost: IWizardDirectCost = {
        ...initialWizardDirectCost,
        id: key.toString()
      }
      setInputValuesDc([...inputValuesDc, { quantity: '', unit_hours: '', unit_price: '' }])
      setDirectCostsList([...directCostsList, cost])
    } else {
      const key = passThroughCostsList.length !== 0 ? Math.max(...passThroughCostsList.map(d => +d.id)) + 1 : 0
      const cost: IWizardPassThroughCost = {
        ...initialWizardPassThroughCost,
        id: key.toString()
      }
      setInputValuesPtc([...inputValuesPtc, { quantity: '', unit_price: '' }])
      setPassThroughCostsList([...passThroughCostsList, cost])
    }
  }

  const renderContent = () => {
    return (
      <div>
        <Grid>
          <label style={{ display: 'flex', alignItems: 'center' }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>Input Options</p>
          </label>
          {renderInputOptions()}
        </Grid>
        {selectedInputOption === 'clone' ?
          <Grid style={{ marginTop: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <p style={{ fontSize: '1.28rem', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#232C58' }}>Select Project</p>
            </label>
            {renderCloneTaskForm()}
          </Grid> : null}
        <Grid>
          {selectedInputOption !== '' ? renderCostTabs() : null}
        </Grid>
        <div className="wizard-enter-costs" style={{ margin: '2rem -1.5rem 4rem' }}>
          {activeItem === 'direct costs' ?
            <div onScroll={() => setInputFocusedId('')} style={directCostsList.length > 5 ? { overflow: 'clip auto', maxHeight: '20.57rem', scrollbarWidth: 'thin', marginRight: '-18px', paddingRight: '7px' } : undefined}>
              <Form>{renderDirectCostForm()}</Form>
            </div> :
            <div onScroll={() => setInputFocusedId('')} style={passThroughCostsList.length > 5 ? { overflow: 'clip auto', maxHeight: '20.57rem', scrollbarWidth: 'thin', marginRight: '-18px', paddingRight: '7px' } : undefined}>
              <Form>{renderPassThroughCostForm()}</Form>
            </div>
          }
          <Button
            disabled={selectedInputOption === '' || activeItem === ''}
            className='button-add-item'
            inverted
            size='small'
            color='teal'
            onClick={() => addCost()}
            content='Add Cost'
            icon={<Icon name='plus' />}
            style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: '0rem', color: '#08D6A1', backgroundColor: '#CDF7EC' }}
          />
          {activeItem === 'pass through costs' && passThroughCostsList.length !== 0 ? renderTotal() : null}
          {activeItem === 'direct costs' && directCostsList.length !== 0 ? renderSubtotal() : null}
        </div>
      </div>
    )
  }

  const disableNext = () => {
    if (passThroughCostsList.length === 0 && directCostsList.length === 0) {
      return true
    }

    if (directCostsList.length !== 0 && passThroughCostsList.length !== 0) {
      return !(directCostsList.length > 0 && directCostsList.every(cost => cost.offer_position !== '' && cost.phase.id !== '' && cost.phase.id !== -1
        && cost.department.id !== '' && cost.department.id !== -1 && cost.unit_price && cost.quantity) && passThroughCostsList.length > 0 && passThroughCostsList.every(cost => cost.offer_position !== '' && cost.offer_position.length <= maxLength25 && cost.phases.filter(p => p.name !== '').length !== 0
          && cost.unit_price && cost.quantity))
    } else if (directCostsList.length !== 0) {
      return !(directCostsList.length > 0 && directCostsList.every(cost => cost.offer_position !== '' && cost.phase.id !== '' && cost.phase.id !== -1
        && cost.department.id !== '' && cost.department.id !== -1 && cost.unit_price && cost.quantity))
    } else if (passThroughCostsList.length !== 0) {
      return !(passThroughCostsList.length > 0 && passThroughCostsList.every(cost => cost.offer_position !== '' && cost.offer_position.length <= maxLength25 && cost.supplier.length < maxLength20 && cost.phases.filter(p => p.name !== '').length !== 0
        && cost.unit_price && cost.quantity))
    }
  }

  const renderFooter = () => {
    return (
      <div>
        <Button
          inverted
          color='grey'
          size='small'
          onClick={goToPreviousStep}
          content='Back'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0, marginRight: '1rem', color: '#747678', borderColor: '#BEC8C3' }}
        />
        <Button
          disabled={disableNext()}
          primary={true}
          size='small'
          onClick={onClickToFinish}
          content='Finish'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0 }}
        />
      </div>
    )
  }

  const handleOnChangeProject = (e: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
    if (data && data.value) {
      const _val: number = data.value as number
      projects.forEach(item => {
        if (+item.id === +_val) {
          setSelectedProject(item)
        }
      })
    }
  }

  const handleAddTasks = () => {
    if (selectedProject) {
      props.getDirectCosts(+selectedProject.id).then(resp => {
        const key = directCostsList.length !== 0 ? Math.max(...directCostsList.map(d => +d.id)) + 1 : 0
        const _directCosts: IWizardDirectCost[] = resp.map((d, idx) => {
          clonedTasks.push(d.offer_position)
          setClonedTasks(clonedTasks)
          return {
            id: (key + idx).toString(),
            key: d.key ?? '',
            offer_position: d.offer_position,
            phase: initialwizardProjectPhase,
            department: d.department,
            unit: d.unit ?? '',
            quantity: d.quantity,
            unit_hours: d.unit_hours,
            unit_price: d.unit_price,
            start_date: d.start_date,
            end_date: d.end_date,
          }
        })
        const _filteredCosts = directCostsList.filter(d => d.id !== '0' || d.offer_position !== '')
        const _dcCosts = [..._filteredCosts, ..._directCosts]
        const _dcInput = _dcCosts.map(dc => {
          return {
            unit_price: dc.unit_price.toString().replace('.', ','),
            unit_hours: dc.unit_hours.toString().replace('.', ','),
            quantity: dc.quantity.toString()
          }
        })
        setInputValuesDc(_dcInput)
        setDirectCostsList(_dcCosts)
      })
      props.getPassTroughCosts(+selectedProject.id).then(resp => {
        const key = passThroughCostsList.length !== 0 ? Math.max(...passThroughCostsList.map(d => +d.id)) + 1 : 0
        const _ptCosts: IWizardPassThroughCost[] = resp.map((d, idx) => {
          clonedTasks.push(d.offer_position)
          setClonedTasks(clonedTasks)
          return {
            id: (key + idx).toString(),
            offer_position: d.offer_position,
            phases: [],
            supplier: d.supplier,
            unit: d.unit ?? '',
            quantity: d.quantity,
            unit_price: d.unit_price,
            start_date: d.start_date,
            end_date: d.end_date,
          }
        })
        const _filteredCosts = passThroughCostsList.filter(d => d.id !== '0' || d.offer_position !== '')
        const _ptcCosts = [..._filteredCosts, ..._ptCosts]
        const _ptcInput = _ptcCosts.map(dc => {
          return {
            unit_price: dc.unit_price.toString().replace('.', ','),
            quantity: dc.quantity.toString()
          }
        })
        setInputValuesPtc(_ptcInput)
        setPassThroughCostsList(_ptcCosts)
      })
      setProjectCloned(true)
    }
  }

  const renderCloneTaskForm = () => {
    if (props.user) {
      const _options = toCloneProjectOptions(cloneSourceProjects(projects, props.user, true), selectedProject?.id)
      return (
        <Grid.Row columns={2} style={{ paddingTop: '.3rem' }}>
          <Form style={{ width: '100%', paddingLeft: '1rem' }}>
            <Form.Group widths='equal'>
              <Form.Field>
                <DropdownComponent
                  search={true}
                  title='Projects'
                  width='auto'
                  changeOption={handleOnChangeProject}
                  options={_options}
                  placeholder='Select project'
                  value={selectedProject?.id}
                  disabledDropdown={_options.length === 0 || projectCloned}
                  icon={ <Icon name='caret down' style={{color: '#08D6A1', fontSize: '1.2rem', position: 'fixed', right: '5px', zIndex: 2, cursor: 'pointer' }}/>}
                />
              </Form.Field>
              <Form.Field>
                <Button
                  disabled={!selectedProject || projectCloned}
                  primary={true}
                  size='small'
                  onClick={handleAddTasks}
                  content='Clone Project'
                  style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0 }}
                />
              </Form.Field>
            </Form.Group>
          </Form>
        </Grid.Row>
      )
    }
    return null
  }

  const onClickToFinish = () => {
    const directCosts = directCostsList.filter(a => a.offer_position !== '').map(a => {
      return {
        ...a,
        id: '',
        start_date: a.phase.start_date ? moment(a.phase.start_date).format('YYYY-MM-DD') : null,
        end_date: a.phase.end_date ? moment(a.phase.end_date).format('YYYY-MM-DD') : null
      }
    })
    const _costPhases: IProjectPhase[] = []
    passThroughCostsList.forEach(c => c.phases.filter(p => p.name !== '').forEach(d => _costPhases.push(d)))
    const _startDates = _costPhases.map(c => moment(c.start_date))
    const _endDates = _costPhases.map(c => moment(c.end_date))
    const passThroughCosts = passThroughCostsList.filter(a => a.offer_position !== '').map(a => {
      return {
        ...a,
        id: '',
        phases: a.phases.filter(p => p.name !== ''),
        start_date: moment(moment.min(_startDates)).format('YYYY-MM-DD'),
        end_date: moment(moment.max(_endDates)).format('YYYY-MM-DD')
      }
    })

    const _project: IWizardProject = {
      ...props.data,
      employees: props.data.permissions === 'open' ? [] : props.data.employees,
      activities: [],
      directCosts: directCosts,
      passThroughCosts: passThroughCosts,
      rebate: internalRebate,
      billing_procedure: props.data.billing_procedure,
      projectClonedState: projectCloned,
      clonedTasksList: clonedTasks,
      phases: props.data.phases.map(phase => {
        return {
          ...phase,
          start_date: phase.start_date ? moment(phase.start_date).format('YYYY-MM-DD') : '',
          end_date: phase.end_date ? moment(phase.end_date).format('YYYY-MM-DD') : ''
        }
      })
    }
    delete _project.cost_tab
    delete _project.input_option
    props.setProjectWizardData(_project)
    props.wizardCreateProject(_project)
  }

  const goToPreviousStep = () => {
    const _data: IWizardProject = {
      ...props.data,
      directCosts: directCostsList,
      passThroughCosts: passThroughCostsList,
      rebate: rebate,
      input_option: selectedInputOption,
      cost_tab: activeItem,
      projectClonedState: projectCloned,
      clonedTasksList: clonedTasks
    }
    props.setWizardStep('step-five')
    props.setProjectWizardData(_data)
  }

  const renderWizard = () => {
    const _data: IWizardProject = {
      ...props.data,
      directCosts: directCostsList,
      passThroughCosts: passThroughCostsList,
      rebate: rebate,
      input_option: selectedInputOption,
      cost_tab: activeItem,
      projectClonedState: projectCloned,
      clonedTasksList: clonedTasks
    }
    const _steps = [
      { key: 1, name: 'step-one' },
      { key: 2, name: 'step-two' },
      { key: 3, name: 'step-three' },
      { key: 4, name: 'step-four' },
      { key: 5, name: 'step-five' },
      { key: 6, name: 'step-six' }
    ]
    return (
      <WizardComponent
        open={props.open}
        close={props.onClose}
        content={renderContent()}
        headerContent='Enter Project Offer'
        headerColor='#232C58'
        footer={renderFooter()}
        steps={6}
        activeStep={6}
        width={'1300px'}
        finishedSteps={props.wizardFinishedSteps}
        setWizardStep={props.setWizardStep}
        setProjectWizardData={props.setProjectWizardData}
        data={_data}
        stepStringList={_steps}
        headerContentFontSize='28px'
      />
    )
  }

  return renderWizard()
}

export default WizardEnterCosts
